import {Stack} from "@src/CommonComponents/Stack/Stack";
import styles from "./overtimeRequests.module.less";

import {useCallback, useEffect, useMemo, useState} from "react";
import {Button, ButtonType} from "@UI/components/controls/Button/Button.tsx";
import {Gt} from "@src/i18n/Gt.tsx";
import {useTranslation} from "react-i18next";
import cn from "classnames";
import {useNavigate} from "react-router-dom";
import {useMutation} from "@tanstack/react-query";
import {AxiosError} from "axios";
import {apiClient} from "@src/httpClient/api.ts";
import {useRequestAction} from "../useRequestAction.ts"
import {getApiErrorMessage} from "@src/httpClient/utils/apiError.ts"
import {
    OvertimeRequest as OvertimeRequestType,
    OvertimeRequestRequest,
    RequestTypeEnum, RequestStatusEnum,
} from "@src/httpClient/__generated__/Api.ts";
import {queryClient} from "@src/QueryClient/queryClient.ts";
import {useParams} from "react-router";
import {useEntityQuery} from "@src/httpClient/utils/queryHelpers.ts";
import {Body_14_Coral_Empasis_14, Body_14_Coral_Primary_Regular_14} from "@UI/typography/generated/typography.tsx";
import {TextInput} from "@src/CommonComponents/Input/TextInput";
import {Textarea} from "@src/CommonComponents/Input/Textarea.tsx";
import {useCredentialsStore} from "@src/Store/userStore.ts";
import {ColorNames} from "@UI/theme/generated/ColorNames.ts";
import {AvatarWithInfo} from "@src/Pages/SettingsPage/MainMenu.tsx";
import {Checked} from "@UI/components/icons/Checked.tsx";
import {Trash} from "@UI/components/icons/Trash.tsx";
import {Xcross} from "@UI/components/icons/Xcross.tsx";
import {useErrorStore} from "@src/Layers/ErrorLayer/ErrorStore.ts";
import {BackButtonRow} from "@src/CommonComponents/HeaderWithBackButton/HeaderWithBackButton.tsx";

const requestTypeLabels: Record<RequestTypeEnum, string> = {
    [RequestTypeEnum.Pre]: 'Pre-approval',
    [RequestTypeEnum.Post]: 'Post-approval',
}

const RequestTypeSelector = ({value, onChange, disabled}: {
    value: RequestTypeEnum,
    onChange: (v: RequestTypeEnum) => void,
    disabled?: boolean,
}) => {
    return (
        <div className={styles.typeSelector}>
            {Object.values(RequestTypeEnum).map(rt => (
                <Button
                    key={rt}
                    type={value === rt ? ButtonType.secondary : ButtonType.third}
                    className={cn(styles.typeChip, {[styles.typeChipActive]: value === rt})}
                    onClick={() => !disabled && onChange(rt)}
                    disabled={disabled}
                >
                    <Gt>{requestTypeLabels[rt]}</Gt>
                </Button>
            ))}
        </div>
    )
}

interface FormState {
    date: string
    start_time: string
    end_time: string
    overtime_hours: string
    reason: string
}

const emptyForm: FormState = {
    date: '',
    start_time: '',
    end_time: '',
    overtime_hours: '',
    reason: '',
}

export const OvertimeRequest = () => {
    const {requestId} = useParams<{ requestId: string }>()
    const navigate = useNavigate()
    const employee = useCredentialsStore(x => x.employee)

    const {t} = useTranslation()
    const setAppError = useErrorStore(x => x.setAppError)
    const isViewMode = !!requestId
    const isManager = !!employee?.is_manager
    const {actionInFlight, runAction} = useRequestAction("overtimeRequest", "/requests/?tab=overtime")
    const [rejectReason, setRejectReason] = useState('')
    const [requestType, setRequestType] = useState<RequestTypeEnum>(RequestTypeEnum.Pre)
    const [form, setForm] = useState<FormState>(emptyForm)
    const [errors, setErrors] = useState<Record<string, string>>({})

    // Load existing request
    const {data} = useEntityQuery<OvertimeRequestType>(
        ["overtimeRequest", requestId],
        async () => await apiClient.api.workflowOvertimeRequestsRetrieve(Number(requestId))
    )

    const isOwnRequest = !isViewMode || (data?.employee === employee?.id)
    const isManagerReview = isViewMode && isManager && !isOwnRequest

    // Populate form from loaded data
    useEffect(() => {
        if (!data) return
        setRequestType(data.request_type ?? RequestTypeEnum.Pre)
        setForm({
            date: data.date ?? '',
            start_time: data.start_time ?? '',
            end_time: data.end_time ?? '',
            overtime_hours: data.overtime_hours ?? '',
            reason: data.reason ?? '',
        })
    }, [data])

    // Auto-set request type based on date
    useEffect(() => {
        if (!form.date) return
        const today = new Date().toISOString().slice(0, 10)
        setRequestType(form.date > today ? RequestTypeEnum.Pre : RequestTypeEnum.Post)
    }, [form.date])

    const onChange = useCallback((field: keyof FormState, value: string) => {
        setForm(prev => ({...prev, [field]: value}))
        setErrors(prev => {
            const next = {...prev}
            delete next[field]
            return next
        })
    }, [])

    // Auto-calculate overtime_hours from start_time and end_time
    useEffect(() => {
        if (!form.start_time || !form.end_time) {
            setForm(prev => ({...prev, overtime_hours: ''}))
            return
        }
        const [sh, sm] = form.start_time.split(':').map(Number)
        const [eh, em] = form.end_time.split(':').map(Number)
        let diffMinutes = (eh * 60 + em) - (sh * 60 + sm)
        if (diffMinutes < 0) diffMinutes += 24 * 60
        const hours = (diffMinutes / 60).toFixed(2)
        setForm(prev => ({...prev, overtime_hours: hours}))
    }, [form.start_time, form.end_time])

    const validate = (): boolean => {
        const e: Record<string, string> = {}
        if (!form.date) e.date = 'Required'
        if (!form.start_time) e.start_time = 'Required'
        if (!form.end_time) e.end_time = 'Required'
        if (!form.reason) e.reason = 'Required'
        setErrors(e)
        return Object.keys(e).length === 0
    }

    const {mutate, isPending} = useMutation<OvertimeRequestType, AxiosError>({
        mutationKey: ["overtimeRequest", "create"],
        mutationFn: async () => {
            const payload: OvertimeRequestRequest = {
                employee: employee!.id,
                date: form.date,
                start_time: form.start_time || undefined,
                end_time: form.end_time || undefined,
                overtime_hours: form.overtime_hours,
                reason: form.reason,
                request_type: requestType,
            }
            const result = await apiClient.api.workflowOvertimeRequestsCreate(payload)
            return result.data
        },
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ["overtimeRequest"]})
            queryClient.invalidateQueries({queryKey: ["requests-list"]})
            navigate("/requests/?tab=overtime")
        },
        onError: (error: AxiosError) => {
            setAppError({
                axiosError: error,
                message: getApiErrorMessage(error),
                showModal: true,
                retry: async () => mutate(),
            })
        },
    })

    const isFormValid = useMemo(() => {
        return !!(form.date && form.start_time && form.end_time && form.reason)
    }, [form])

    const handleSubmit = useCallback(() => {
        if (!validate()) return
        mutate()
    }, [form, requestType])

    return (
        <Stack direction={"column"} gap={16} className={styles.subMenuPage}>
            <BackButtonRow title={"Back"} backLink={"/requests/?tab=overtime"} hideOnMobile/>
            <AvatarWithInfo employee={isViewMode && data ? {id: data.employee, first_name: data.employee_name} as any : employee}/>

            <Stack direction={"column"} className={styles.whiteBlock} justifyContent={"center"} gap={24}>
                <Stack direction={"column"} gap={6}>
                    <Body_14_Coral_Empasis_14>
                        <Gt>Request type</Gt>
                    </Body_14_Coral_Empasis_14>
                    <RequestTypeSelector
                        value={requestType}
                        onChange={setRequestType}
                        disabled
                    />
                </Stack>
            </Stack>

            <Stack direction={"column"} className={styles.whiteBlock} justifyContent={"center"} gap={24}>
                <Stack direction={"column"} gap={6}>
                    <Body_14_Coral_Empasis_14>
                        <Gt>Date</Gt>
                    </Body_14_Coral_Empasis_14>
                    <TextInput
                        name="date"
                        value={form.date}
                        onChange={v => onChange('date', v)}
                        type="date"
                        hasError={!!errors.date}
                        disabled={isViewMode}
                    />
                </Stack>

                <Stack direction={"column"} gap={6}>
                    <Body_14_Coral_Empasis_14>
                        <Gt>Start time</Gt>
                    </Body_14_Coral_Empasis_14>
                    <TextInput
                        name="start_time"
                        value={form.start_time}
                        onChange={v => onChange('start_time', v)}
                        type="time"
                        disabled={isViewMode}
                    />
                </Stack>

                <Stack direction={"column"} gap={6}>
                    <Body_14_Coral_Empasis_14>
                        <Gt>End time</Gt>
                    </Body_14_Coral_Empasis_14>
                    <TextInput
                        name="end_time"
                        value={form.end_time}
                        onChange={v => onChange('end_time', v)}
                        type="time"
                        disabled={isViewMode}
                    />
                </Stack>

            </Stack>

            <Stack direction={"column"} className={styles.whiteBlock} gap={24}>
                <Body_14_Coral_Empasis_14>
                    <Gt>Reason</Gt>
                </Body_14_Coral_Empasis_14>
                <Textarea
                    name="reason"
                    initialValue={form.reason}
                    onValueChange={v => onChange('reason', v as string)}
                    disabled={isViewMode}
                />
            </Stack>

            {isViewMode && data?.status === RequestStatusEnum.Rejected && (
                <Stack direction={"column"} className={styles.whiteBlock} gap={12}>
                    <Stack direction={"column"} gap={6}>
                        <Body_14_Coral_Empasis_14>
                            <Gt>Rejected by</Gt>
                        </Body_14_Coral_Empasis_14>
                        <Body_14_Coral_Primary_Regular_14>
                            {data.reviewed_by_name}
                        </Body_14_Coral_Primary_Regular_14>
                    </Stack>
                    {data.reject_reason && (
                        <Stack direction={"column"} gap={6}>
                            <Body_14_Coral_Empasis_14>
                                <Gt>Rejection reason</Gt>
                            </Body_14_Coral_Empasis_14>
                            <Body_14_Coral_Primary_Regular_14>
                                {data.reject_reason}
                            </Body_14_Coral_Primary_Regular_14>
                        </Stack>
                    )}
                </Stack>
            )}

            {!isViewMode && (
                <Button
                    icon={<Checked/>}
                    type={ButtonType.secondary}
                    color={ColorNames.CORAL_GREEN}
                    className={styles.submitButton}
                    onClick={handleSubmit}
                    disabled={isPending || !employee?.id || !isFormValid}
                >
                    <Gt>Submit application</Gt>
                </Button>
            )}

            {isViewMode && data?.status === RequestStatusEnum.Pending && isOwnRequest && (
                <Button
                    icon={<Trash/>}
                    type={ButtonType.secondary}
                    color={ColorNames.CORAL_FIRE}
                    className={styles.submitButton}
                    disabled={actionInFlight}
                    onClick={() => runAction(() => apiClient.api.workflowOvertimeRequestsCancelCreate(Number(requestId)))}
                >
                    <Gt>Cancel request</Gt>
                </Button>
            )}

            {isManagerReview && data?.status === RequestStatusEnum.Pending && (
                <>
                    <Textarea
                        name="reject_reason"
                        initialValue={rejectReason}
                        onValueChange={v => setRejectReason(v as string)}
                        placeholder={t('Rejection reason')}
                    />
                    <div className={styles.actionButtons}>
                        <Button
                            icon={<Checked/>}
                            type={ButtonType.secondary}
                            color={ColorNames.CORAL_GREEN}
                            className={styles.actionButton}
                            disabled={actionInFlight}
                            onClick={() => runAction(() => apiClient.api.workflowOvertimeRequestsApproveCreate(Number(requestId)))}
                        >
                            <Gt>Approve</Gt>
                        </Button>
                        <Button
                            icon={<Xcross/>}
                            type={ButtonType.secondary}
                            color={ColorNames.CORAL_FIRE}
                            className={styles.actionButton}
                            disabled={!rejectReason || actionInFlight}
                            onClick={() => runAction(() => apiClient.api.workflowOvertimeRequestsRejectCreate(Number(requestId), {reason: rejectReason}))}
                        >
                            <Gt>Reject</Gt>
                        </Button>
                    </div>
                </>
            )}
        </Stack>
    )
}
